import { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { supabase } from '../../lib/auth/supabase';

interface SessionManagerProps {
  children: React.ReactNode;
  onSessionExpired?: () => void;
  refreshInterval?: number; // in minutes
}

export const SessionManager: React.FC<SessionManagerProps> = ({ 
  children, 
  onSessionExpired,
  refreshInterval = 50 // Default to 50 minutes (tokens typically expire after 60)
}) => {
  const { isAuthenticated, logout } = useAuth();
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear any existing timers if not authenticated
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      return;
    }

    // Function to refresh the session
    const refreshSession = async () => {
      try {
        const { data, error } = await supabase.auth.refreshSession();
        
        if (error) {
          console.error('Session refresh error:', error);
          onSessionExpired?.();
          logout();
          return;
        }

        if (!data.session) {
          console.log('No session to refresh');
          onSessionExpired?.();
          logout();
          return;
        }

        console.log('Session refreshed successfully');
      } catch (error) {
        console.error('Unexpected error refreshing session:', error);
      }
    };

    // Set up periodic session refresh
    refreshTimerRef.current = setInterval(refreshSession, refreshInterval * 60 * 1000);

    // Also refresh on visibility change (when user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        refreshSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, logout, onSessionExpired, refreshInterval]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: authListener } = supabase.auth.onAuthStateChange((event, _session) => {
      console.log('Auth state changed:', event);
      
      switch (event) {
        case 'SIGNED_OUT':
          // User signed out
          if (refreshTimerRef.current) {
            clearInterval(refreshTimerRef.current);
            refreshTimerRef.current = null;
          }
          break;
          
        case 'TOKEN_REFRESHED':
          console.log('Token refreshed automatically');
          break;
          
        case 'USER_UPDATED':
          console.log('User data updated');
          break;
          
        default:
          break;
      }
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  return <>{children}</>;
}; 